import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from 'design-system-web';
import { AdminPage, Loading } from 'components';
import { Alert, AlertType } from 'components/lds';
import { H3, H4, S } from 'components/typography';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { Breadcrumbs } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeBreadcrumbs';
import { SectionCard } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeSectionCard';
import { SortableSectionCardWrapper, SectionDragHandleIcon } from '../components/inspectionTemplateCustomize/SortableSectionCardWrapper';
import { InspectionTemplateCustomizeSidebar } from '../components/inspectionTemplateCustomize/InspectionTemplateCustomizeSidebar';
import { useInspectionTemplateCustomizePage } from '../hooks/inspectionTemplateCustomize/useInspectionTemplateCustomizePage';
import styles from './InspectionTemplateCustomizePage.scss';

type Props = {
  channelBasePath: string;
};

export function InspectionTemplateCustomizePage({ channelBasePath }: Props) {
  const { t } = useTranslation();

  const {
    activeSectionId,
    called,
    error,
    expandedBySectionId,
    handleCancel,
    hasValidRouteContext,
    inspectionsTemplateLibraryEnabled,
    lastUpdatedText,
    listPageUrl,
    loading,
    mainColumnRefCallback,
    removeItemDisabledTitle,
    removeSectionDisabledTitle,
    removeTemplateItem,
    removeTemplateSection,
    removedOverlayItemIds,
    removedSectionPresentationIds,
    removingItemIds,
    removingSectionIds,
    restoreTemplateItem,
    restoreTemplateSection,
    restoringItemIds,
    restoringSectionIds,
    sortedSections,
    summary,
    template: inspectionTemplate,
    toggleSection,
    reorderEnabled,
    isReorderPending,
    onReorderSections,
    onReorderItemsInSection,
  } = useInspectionTemplateCustomizePage(channelBasePath);

  const sectionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(null);
  const [draggingSectionWidth, setDraggingSectionWidth] = useState<number | undefined>();

  // Derive at render time so the overlay always reflects the current sortedSections,
  // even if an optimistic reorder completes while the drag is in flight.
  const draggingSection = draggingSectionId
    ? (sortedSections.find(s => s.id === draggingSectionId) ?? null)
    : null;

  const handleSectionDragStart = useCallback(
    (event: DragStartEvent) => {
      setDraggingSectionId(String(event.active.id));
      setDraggingSectionWidth(event.active.rect.current.initial?.width ?? undefined);
    },
    []
  );

  const handleSectionDragCancel = useCallback(() => {
    setDraggingSectionId(null);
  }, []);

  const handleSectionDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingSectionId(null);
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      onReorderSections(String(active.id), String(over.id)).catch(() => {});
    },
    [onReorderSections]
  );

  const handleItemReorder = useCallback(
    (sectionId: string, activeId: string, overId: string) => {
      onReorderItemsInSection(sectionId, activeId, overId).catch(() => {});
    },
    [onReorderItemsInSection]
  );

  const restoreSectionBlocked = Boolean(
    isReorderPending ||
    removingSectionIds.size > 0 ||
      restoringSectionIds.size > 0 ||
      restoringItemIds.size > 0 ||
      removingItemIds.size > 0
  );
  const restoreSectionBlockedHint = restoreSectionBlocked
    ? t(
        'web.admin.inspections.templateCustomize.restoreSectionBlockedHint'
      )
    : '';

  const renderSection = (
    section: typeof sortedSections[number],
    dragHandleNode: React.ReactNode
  ) => {
    const isRemoved = removedSectionPresentationIds.has(section.id);
    const onRemoveSection = isRemoved
      ? undefined
      : () => handleRemoveSection(section.id);
    const onRestoreSection =
      inspectionsTemplateLibraryEnabled && isRemoved && !isReorderPending
        ? () => handleRestoreSection(section.id)
        : undefined;
    const onReorderItems = reorderEnabled
      ? (activeId: string, overId: string) =>
          handleItemReorder(section.id, activeId, overId)
      : undefined;

    return (
      <SectionCard
        key={section.id}
        section={section}
        expanded={expandedBySectionId[section.id] ?? true}
        onToggle={() => toggleSection(section.id)}
        isSectionRemovedPresentation={isRemoved}
        sectionRemoveDisabled={isReorderPending}
        removeSectionDisabledTitle={removeSectionDisabledTitle}
        removeItemDisabledTitle={removeItemDisabledTitle}
        onRemoveSection={onRemoveSection}
        isRemoveSectionLoading={removingSectionIds.has(section.id)}
        onRestoreSection={onRestoreSection}
        isRestoreSectionLoading={restoringSectionIds.has(section.id)}
        restoreSectionDisabled={restoreSectionBlocked}
        restoreSectionDisabledTitle={restoreSectionBlockedHint}
        onRemoveItem={isReorderPending ? undefined : handleRemoveItem}
        removingItemIds={removingItemIds}
        onRestoreItem={isReorderPending ? undefined : handleRestoreItem}
        restoringItemIds={restoringItemIds}
        removedOverlayItemIds={removedOverlayItemIds}
        dragHandleNode={dragHandleNode}
        reorderEnabled={reorderEnabled}
        onReorderItems={onReorderItems}
      />
    );
  };

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      removeTemplateItem(itemId).catch(() => {});
    },
    [removeTemplateItem]
  );

  const handleRestoreItem = useCallback(
    (itemId: string) => {
      restoreTemplateItem(itemId).catch(() => {});
    },
    [restoreTemplateItem]
  );

  const handleRemoveSection = useCallback(
    (sectionId: string) => {
      removeTemplateSection(sectionId).catch(() => {});
    },
    [removeTemplateSection]
  );

  const handleRestoreSection = useCallback(
    (sectionId: string) => {
      restoreTemplateSection(sectionId).catch(() => {});
    },
    [restoreTemplateSection]
  );

  if (!hasValidRouteContext) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Alert type={AlertType.error} fullWidth>
            {t('web.admin.inspections.templateCustomize.missingContext')}
          </Alert>
        </div>
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.page}>
          <Breadcrumbs listPageUrl={listPageUrl} />
          <Alert
            type={AlertType.error}
            fullWidth
            className={styles.alertSpacing}
          >
            {t('web.admin.inspections.templateCustomize.error')}
          </Alert>
          <Button variant="secondary" size="large" onClick={handleCancel}>
            {t('web.admin.inspections.templateCustomize.close')}
          </Button>
        </div>
      </AdminPage>
    );
  }

  if ((loading || !called) && !inspectionTemplate) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  if (called && !loading && !inspectionTemplate) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.page}>
          <Breadcrumbs listPageUrl={listPageUrl} />
          <Alert
            type={AlertType.error}
            fullWidth
            className={styles.alertSpacing}
          >
            {t('web.admin.inspections.templateCustomize.notFound')}
          </Alert>
          <Button variant="secondary" size="large" onClick={handleCancel}>
            {t('web.admin.inspections.templateCustomize.close')}
          </Button>
        </div>
      </AdminPage>
    );
  }

  if (!inspectionTemplate) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.loadingOrError}>
          <Loading />
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.page}>
        <Breadcrumbs listPageUrl={listPageUrl} />

        <div className={styles.pageTitle}>
          <H3>{inspectionTemplate.metadata.name}</H3>
          <S variant="secondary">
            {t('web.admin.inspections.templateCustomize.lastUpdated', {
              date: lastUpdatedText,
            })}
          </S>
        </div>

        <div className={styles.contentRow}>
          <div ref={mainColumnRefCallback} className={styles.mainColumn}>
            <div className={styles.alertWrap}>
              <Alert type={AlertType.info} fullWidth className={styles.alertBanner}>
                {t('web.admin.inspections.templateCustomize.instanceOnlyNotice')}
              </Alert>
            </div>

            <div className={styles.questionsHeadingText}>
              <H4>
                {t('web.admin.inspections.templateCustomize.questionsHeading')}
              </H4>
              <S variant="secondary">{summary}</S>
            </div>

            {reorderEnabled ? (
              <DndContext
                sensors={sectionSensors}
                collisionDetection={closestCenter}
                onDragStart={handleSectionDragStart}
                onDragEnd={handleSectionDragEnd}
                onDragCancel={handleSectionDragCancel}
              >
                <SortableContext
                  items={sortedSections.map(section => section.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortedSections.map(section => (
                    <SortableSectionCardWrapper
                      key={section.id}
                      sectionId={section.id}
                      reorderEnabled
                    >
                      {dragHandleNode =>
                        renderSection(section, dragHandleNode)
                      }
                    </SortableSectionCardWrapper>
                  ))}
                </SortableContext>
                <DragOverlay>
                  {draggingSection && (
                    <div style={{ width: draggingSectionWidth }}>
                      <SectionCard
                        section={draggingSection}
                        expanded={expandedBySectionId[draggingSection.id] ?? true}
                        onToggle={() => {}}
                        isSectionRemovedPresentation={removedSectionPresentationIds.has(draggingSection.id)}
                        sectionRemoveDisabled
                        removeSectionDisabledTitle=""
                        removeItemDisabledTitle=""
                        removingItemIds={removingItemIds}
                        removedOverlayItemIds={removedOverlayItemIds}
                        dragHandleNode={<SectionDragHandleIcon />}
                        reorderEnabled={false}
                        showStaticItemDragHandles
                      />
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            ) : (
              sortedSections.map(section => renderSection(section, null))
            )}
          </div>

          <InspectionTemplateCustomizeSidebar
            sections={sortedSections}
            activeSectionId={activeSectionId}
            removedSectionPresentationIds={removedSectionPresentationIds}
          />
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" size="large" onClick={handleCancel}>
            {t('web.admin.inspections.templateCustomize.close')}
          </Button>
        </div>
      </div>
    </AdminPage>
  );
}
